import { createMRTColumnHelper, MRT_ColumnDef } from "material-react-table";
import { useTranslation } from "react-i18next";
import { GridText } from "src/components/Grid/GridText";
import { EmptyNullFilter } from "src/components/Grid/filters/EmptyNullFilter";
import type { NetworkMember } from "src/api/client-api/network/useNetworkLists";
import { NetworkOptionsFilter, optionLabelsChip, type NetworkOption } from "./networkFilters";
import { useShortDateFormatter } from "./useShortDateFormatter";

export const networkMemberName = (member: Pick<NetworkMember, "firstname" | "lastname">) =>
    [member.firstname, member.lastname].filter(Boolean).join(" ");

export const useNetworkMemberColumns = (organizations: NetworkOption[]): MRT_ColumnDef<NetworkMember, any>[] => {
    const { t } = useTranslation();
    const formatDate = useShortDateFormatter();
    const columnHelper = createMRTColumnHelper<NetworkMember>();

    const emptyNullChip = (emptyLabel: string, notEmptyLabel: string) => (value: unknown) => {
        const operator = typeof value === "object" && value !== null && "emptyNull" in value ? value.emptyNull : value;
        if (operator === "empty") return emptyLabel;
        if (operator === "notEmpty") return notEmptyLabel;
        return null;
    };

    return [
        columnHelper.accessor((row) => networkMemberName(row), {
            id: "lastname",
            header: t("network.column.name", "Name"),
            enableColumnFilter: false,
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
        }),
        columnHelper.accessor("email", {
            id: "email",
            header: t("email"),
            enableColumnFilter: false,
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
        }),
        columnHelper.accessor((row) => row.organizationName, {
            id: "organization",
            header: t("network.column.organization", "Organization"),
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
            Filter: (mrtProps) => <NetworkOptionsFilter mrtProps={mrtProps} options={organizations} />,
            meta: { renderFilterChipValue: optionLabelsChip(organizations) },
        }),
        columnHelper.accessor((row) => row.organizationExternalId ?? "", {
            id: "organizationExternalId",
            header: t("unique_identifier"),
            enableColumnFilter: false,
            enableSorting: false,
            meta: { visibleByDefault: false },
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
        }),
        columnHelper.accessor((row) => row.parentOrganizationNames ?? "", {
            id: "parentOrganization",
            header: t("network.column.parent_organization", "Parent organization"),
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
            Filter: (mrtProps) => <NetworkOptionsFilter mrtProps={mrtProps} options={organizations} />,
            meta: { renderFilterChipValue: optionLabelsChip(organizations) },
        }),
        columnHelper.accessor((row) => row.lastLoginAt ?? "", {
            id: "lastLoginAt",
            header: t("network.column.last_login", "Last login"),
            Cell: ({ row }) => (
                <GridText
                    appearance="product-v2"
                    value={
                        row.original.lastLoginAt ?
                            formatDate(row.original.lastLoginAt)
                        :   t("network.last_login.never", "Never")
                    }
                />
            ),
            Filter: (mrtProps) => <EmptyNullFilter {...mrtProps} />,
            meta: {
                renderFilterChipValue: emptyNullChip(
                    t("network.last_login.never", "Never"),
                    t("network.last_login.logged_in", "Logged in"),
                ),
            },
        }),
        columnHelper.accessor("accountCreatedAt", {
            id: "accountCreatedAt",
            header: t("network.column.account_created", "Account created"),
            enableColumnFilter: false,
            enableSorting: false,
            meta: { visibleByDefault: false },
            Cell: ({ row }) => <GridText appearance="product-v2" value={formatDate(row.original.accountCreatedAt)} />,
        }),
        columnHelper.accessor((row) => row.termsAcceptedAt ?? "", {
            id: "termsAcceptedAt",
            header: t("network.column.terms_accepted", "Terms accepted"),
            Cell: ({ row }) => (
                <GridText
                    appearance="product-v2"
                    value={
                        row.original.termsAcceptedAt ?
                            formatDate(row.original.termsAcceptedAt)
                        :   t("network.terms.not_accepted", "Not accepted")
                    }
                />
            ),
            Filter: (mrtProps) => <EmptyNullFilter {...mrtProps} />,
            meta: {
                renderFilterChipValue: emptyNullChip(
                    t("network.terms.not_accepted", "Not accepted"),
                    t("network.terms.accepted", "Accepted"),
                ),
            },
        }),
    ];
};
